/*
 * Shot-rule config for the Notes tab. Maps each videographer preference
 * (scheduledEvent.preferences.*) — whose `option` is a number (1..3) — to a
 * short tag text, a tone, and a specific tooltip. Texts/tooltips/tones are
 * lifted from the legacy videographer preferences view; EDIT HERE to tweak.
 *
 * Tone meaning: green = allowed, red = not allowed, amber = allowed under
 * certain circumstances. Tones don't always follow "1=green/2=amber/3=red" —
 * they mirror the legacy colors exactly (e.g. eventEnding: 1 & 3 green, 2 red).
 */

export type Tone = 'amber' | 'green' | 'red';

export interface OptionRule {
  tone: Tone;
  /** Short tag label. */
  text: string;
  /** Specific tooltip text. */
  tooltip: string;
}

export type PreferenceKey =
  | 'audienceShots'
  | 'closeUpShots'
  | 'detailedViewShots'
  | 'eventEnding'
  | 'quickShots'
  | 'wideVenueShots';

export interface ShotRule {
  key: PreferenceKey;
  /** Keyed by the preference's `option` number. */
  options: Record<number, OptionRule>;
}

export const SHOT_RULES: ShotRule[] = [
  {
    key: 'quickShots',
    options: {
      1: {
        tone: 'green',
        text: 'Quick Shots',
        tooltip:
          'Use Quickshots & live joystick for moments and effects when it makes sense with the action.'
      },
      2: {
        tone: 'red',
        text: 'No Quick Shots',
        tooltip: 'Do not use Quick Shots or any live movements during the event.'
      }
    }
  },
  {
    key: 'closeUpShots',
    options: {
      1: {
        tone: 'green',
        text: 'Close ups',
        tooltip:
          'Capture close-ups of individuals or action. Include mid and wide shots but primarily use close-ups when possible.'
      },
      2: {
        tone: 'red',
        text: 'No close ups',
        tooltip: 'Do not capture close-ups. Stick to medium and wide shots.'
      }
    }
  },
  {
    key: 'wideVenueShots',
    options: {
      1: {
        tone: 'green',
        text: 'Wide shots',
        tooltip: 'Capture wide shots when possible to show group action and the venue.'
      },
      2: {
        tone: 'red',
        text: 'No Wide shots',
        tooltip: 'Do not capture wide shots. Stick to medium and close-ups to capture the action.'
      }
    }
  },
  {
    key: 'audienceShots',
    options: {
      1: {
        tone: 'green',
        text: 'Audience shots',
        tooltip: 'Show the audience or congregation when appropriate. Do not show empty seats.'
      },
      2: {
        tone: 'amber',
        text: 'Show audience if required',
        tooltip:
          'Only show the audience when required or specified by a cue sheet or other note. Avoid showing the audience when a close-up or medium shot can be used.'
      },
      3: {
        tone: 'red',
        text: 'No Audience shots',
        tooltip:
          'Do not show the audience whenever possible. If necessary, frame as little of the audience as possible when needed to capture action. Do not show cut-away shots to the audience ever.'
      }
    }
  },
  {
    key: 'detailedViewShots',
    options: {
      1: {
        tone: 'green',
        text: 'Detailed venue shots',
        tooltip:
          'Capture B-roll including details of the venue — stained glass, paintings, flowers, architecture — anything that would add to the interest of the footage.'
      },
      2: {
        tone: 'red',
        text: 'No Detailed venue shots',
        tooltip:
          'Avoid using detailed venue shots or "B-roll" during the event. Keep the focus on the action and/or area the action is taking place.'
      }
    }
  },
  {
    key: 'eventEnding',
    options: {
      1: {
        tone: 'green',
        text: 'End Event: Your Choice',
        tooltip:
          'End the event when you know for certain it is over. Reach out to your Event Support team if you are unsure.'
      },
      2: {
        tone: 'red',
        text: 'End Event: Client Will End',
        tooltip:
          'Do not end the event. The client will end the event in their dashboard. If all action/sound has stopped, reach out to Support for confirmation before ending.'
      },
      3: {
        tone: 'amber',
        text: 'End Event: Spoken Cue',
        tooltip:
          'Wait for the spoken cue to end the event. This will be given by someone on-site as part of the event. Otherwise, do not end.'
      }
    }
  }
];

/*
 * Boolean feature toggles. Not option-based: shown (green) only when true;
 * when false/absent nothing renders (no red tag).
 */
export type ToggleKey = 'montageEnabled' | 'slidesEnabled';

export interface ToggleRule {
  key: ToggleKey;
  text: string;
  tooltip: string;
}

export const SHOT_TOGGLES: ToggleRule[] = [
  {
    key: 'slidesEnabled',
    text: 'Intro/Outro Slides',
    tooltip:
      'Enable the Intro Slide prior to going live and the Outro slide prior to ending the event to ensure the first and last frames are slides.'
  },
  {
    key: 'montageEnabled',
    text: 'Montage Integration',
    tooltip:
      'Use Full Screen graphics to show the video montage or any graphics. Do not capture the screen or monitor on-site.'
  }
];

/** Tone → tag color class + leading icon. */
export const TONE_STYLE: Record<Tone, { className: string; icon: string }> = {
  green: { className: 'cc-green', icon: 'fas fa-check' },
  amber: { className: 'cc-amber', icon: 'fas fa-minus' },
  red: { className: 'cc-red', icon: 'fas fa-times' }
};
